import type { ButtonHTMLAttributes } from "react";
import { cx } from "./cx";

export interface SocialButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  provider: "google" | "facebook";
  /** Defaults to "Continue with Google" / "Continue with Facebook". Only change it for a waiting label. */
  children?: string;
}

const LABEL = { google: "Continue with Google", facebook: "Continue with Facebook" };

/* The providers' own artwork, unmodified: never recoloured, outlined or set in one colour. */
function GoogleG() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true" className="block">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

function FacebookF() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" className="block">
      <path
        fill="#0866FF"
        d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.026 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.971H15.83c-1.491 0-1.956.93-1.956 1.886v2.264h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"
      />
    </svg>
  );
}

/** Third-party sign-in in the providers' neutral outlined treatment, the one component that
 *  leaves the Kalikaya palette and type: their branding rules require it. Both share one
 *  size and style so neither reads as the recommended one. */
export function SocialButton({ provider, children, disabled, className, ...rest }: SocialButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={cx(
        "relative flex h-(--control-lg) w-full cursor-pointer items-center justify-center rounded-sm border bg-white px-11 whitespace-nowrap",
        "[font-family:Roboto,var(--font-body)] text-[14px] leading-5 font-medium tracking-[0.25px] text-[#1F1F1F] [transition:var(--transition-control)]",
        "border-[#747775] enabled:hover:bg-[#F2F2F2] enabled:active:bg-[#E8E8E8]",
        "disabled:cursor-not-allowed disabled:border-[rgba(31,31,31,.12)]",
        className,
      )}
      {...rest}
    >
      <span className={cx("absolute top-1/2 left-[13px] -translate-y-1/2", disabled && "opacity-[.38]")}>{provider === "facebook" ? <FacebookF /> : <GoogleG />}</span>
      <span className={cx(disabled && "opacity-[.38]")}>{children ?? LABEL[provider]}</span>
    </button>
  );
}
